import { useMemo, type ReactNode } from 'react';
import { Plus, X } from 'lucide-react';
import { Button, Input, VirtualizedCombobox } from '@roger/ui-components';
import { CurrencyInput } from '../../CurrencyInput';
import { useValueCodeOptions } from '../../../hooks/useValueCodeOptions';
import type { ValueRow } from './types';

function CodeCell({
  code,
  onCodeChange,
  disabled = false,
}: {
  code: string;
  onCodeChange: (code: string) => void;
  disabled?: boolean;
}) {
  const { options, loading } = useValueCodeOptions();

  const comboOptions = useMemo(() => {
    const base = options.map(o => ({
      value: o.code,
      label: `${o.code} — ${o.description}`,
    }));
    if (code && !options.some(o => o.code === code)) {
      base.unshift({ value: code, label: code });
    }
    return base;
  }, [options, code]);

  if (!loading && options.length === 0) {
    return (
      <Input
        inputSize='sm'
        className='w-28 font-mono'
        placeholder='Code'
        disabled={disabled}
        value={code}
        onChange={e => onCodeChange(e.target.value)}
      />
    );
  }

  return (
    <VirtualizedCombobox
      options={comboOptions}
      value={code}
      triggerLabel={code || undefined}
      onValueChange={next => {
        if (next === '') return;
        onCodeChange(next);
      }}
      placeholder='Code'
      searchPlaceholder='Search value codes...'
      isLoading={loading}
      disabled={disabled}
      size='sm'
      className='w-28'
      popoverWidth='420px'
    />
  );
}

export function ValueCodesField({
  rows,
  onChange,
  addRowActions,
  readOnly = false,
}: {
  rows: ValueRow[];
  onChange: (next: ValueRow[]) => void;
  /** Optional extra buttons rendered next to the Add Value Code button. */
  addRowActions?: ReactNode;
  readOnly?: boolean;
}) {
  const update = (i: number, patch: Partial<ValueRow>) =>
    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const remove = (i: number) => onChange(rows.filter((_, idx) => idx !== i));
  const add = () => onChange([...rows, { code: '', amount: '' }]);

  return (
    <div className='flex flex-col gap-2'>
      <div className='flex items-center gap-2 text-xs font-medium text-muted-foreground'>
        <span className='w-28'>Value Code</span>
        <span className='w-28'>Amount</span>
      </div>
      {rows.map((entry, index) => (
        <div key={index} className='flex items-center gap-2'>
          <CodeCell
            code={entry.code}
            disabled={readOnly}
            onCodeChange={code => update(index, { code })}
          />
          <CurrencyInput
            inputSize='sm'
            className='w-28'
            placeholder='0.00'
            disabled={readOnly}
            value={entry.amount}
            onValueChange={amount => update(index, { amount })}
          />
          <Button
            type='button'
            variant='ghost'
            size='icon'
            aria-label='Remove value code'
            disabled={readOnly}
            onClick={() => remove(index)}
          >
            <X className='h-4 w-4' />
          </Button>
        </div>
      ))}
      <div className='flex items-center gap-2 self-start'>
        <Button
          type='button'
          variant='outline'
          size='sm'
          disabled={readOnly}
          onClick={add}
        >
          <Plus className='mr-1 h-3 w-3' />
          Add Value Code
        </Button>
        {addRowActions}
      </div>
    </div>
  );
}
